<!-- CompletionSteps.vue -->
<template>
  <v-flex xs12 sm12 md12>
    <v-container>
      <v-layout column :class="{ 'is-loading': loading }">
        <!-- Rows -->
        <template v-for="row in rows">
          <v-layout row wrap align-center :key="row.id || row.step">
            <!-- Checkbox -->
            <v-flex xs12 sm6 md4>
              <v-checkbox
                :input-value="row.completed"
                :disabled="isDisabled"
                color="#8bc34a"
                hide-details
                :label="$t(row.step)"
                @change="onCompletedChange(row.step, $event)"
              />
            </v-flex>

            <!-- Date picker -->
            <v-flex xs12 sm6 md4>
              <v-menu
                v-model="menus[row.step]"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="isDisabled"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-on="on"
                    :value="formatDate(row.date)"
                    :label="$t(dateLabelKey)"
                    prepend-icon="event"
                    readonly
                    hide-details
                    :disabled="isDisabled || !row.completed"
                  />
                </template>

                <v-date-picker
                  :value="row.date"
                  @input="onDatePicked(row.step, $event)"
                  :max="maxDate"
                />
              </v-menu>
            </v-flex>

            <!-- Validation hint -->
            <v-flex xs12 sm12 md4 v-if="showValidation && row.completed && !row.date">
              <div class="red--text caption">{{ $t(requiredDateMsgKey) }}</div>
            </v-flex>
          </v-layout>
        </template>

        <!-- Actions -->
        <v-layout row wrap justify-end v-if="!readonly && !autoSave">
          <v-btn
            depressed
            dark
            color="#253554"
            @click="save"
            :loading="saving || loading"
            :disabled="loading"
          >
            {{ $t(saveLabelKey) }}
          </v-btn>

          <v-btn
            flat
            class="black--text"
            @click="resetToProp"
            :disabled="saving || loading"
            style="display: none;"
          >
            {{ $t(resetLabelKey) }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-container>
  </v-flex>
</template>

<script>
/**
 * CompletionSteps.vue (Vue 2 + Vuetify 1.5)
 *
 * Props:
 *  - value: Array<{ id?: number|string, step: string, completed: boolean, date: string|null }>
 *  - readonly: Boolean
 *  - loading: Boolean             // disables everything + shows spinner on Save
 *  - autoSave: Boolean            // if true, save() after every change
 *  - showValidation: Boolean      // show "date required" when completed && !date
 *  - dateLabelKey, saveLabelKey, resetLabelKey, requiredDateMsgKey: i18n keys
 *
 * Events:
 *  - input  (v-model sync)
 *  - change (fires on every edit)
 *  - save   (parent persists to API/store)
 */
export default {
  name: 'CompletionSteps',
  props: {
    value: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    autoSave: { type: Boolean, default: false },
    showValidation: { type: Boolean, default: true },
    dateLabelKey: { type: String, default: 'date_of_completition' },
    saveLabelKey: { type: String, default: 'save' },
    resetLabelKey: { type: String, default: 'reset' },
    requiredDateMsgKey: { type: String, default: 'date.invalid' }
  },
  data () {
    const initialLocal = this.normalize(this.value)
    return {
      local: initialLocal, // working copy
      menus: {}, // { [stepKey]: boolean }
      saving: false
    }
  },
  created () {
    this.ensureMenuKeys(this.local)
  },
  computed: {
    rows () {
      return this.local || []
    },
    maxDate () {
      const today = new Date()
      return today.toISOString().slice(0, 10)
    },
    isDisabled () {
      return this.readonly || this.loading
    }
  },
  watch: {
    value: {
      deep: true,
      handler (nv) {
        const next = this.normalize(nv)
        this.local = next
        this.ensureMenuKeys(next)
      }
    }
  },
  methods: {
    displayDate (iso) {
      if (!iso) return ''
      // Expecting YYYY-MM-DD; render DD/MM/YYYY
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso))
      if (!m) return String(iso)
      return `${m[3]}/${m[2]}/${m[1]}`
    },
    normalize (arr) {
      return Array.isArray(arr)
        ? arr.map(i => ({
          id: i && i.id != null ? i.id : undefined,
          step: String(i.step), // e.g. 'steps.payment'
          completed: Boolean(i.completed),
          date: i && i.date ? String(i.date).slice(0, 10) : null
        }))
        : []
    },
    ensureMenuKeys (rows) {
      (rows || []).forEach(i => {
        const key = i.step
        if (this.menus[key] === undefined) this.$set(this.menus, key, false)
      })
    },
    formatDate (iso) {
      if (!iso) return ''
      // If a Date object sneaks in, normalize to YYYY-MM-DD first
      if (iso instanceof Date && !isNaN(iso)) {
        const yyyy = iso.getFullYear()
        const mm = String(iso.getMonth() + 1).padStart(2, '0')
        const dd = String(iso.getDate()).padStart(2, '0')
        iso = `${yyyy}-${mm}-${dd}`
      }
      // Expecting YYYY-MM-DD; render DD/MM/YYYY
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso))
      if (!m) return String(iso)
      return `${m[3]}/${m[2]}/${m[1]}`
    },

    updateAndEmit (next) {
      this.local = next
      this.$emit('input', next) // keep live v-model; switch to explicit-only if desired
      this.$emit('change', next)
      if (this.autoSave) this.save()
    },
    onCompletedChange (stepKey, completed) {
      const next = (this.local || []).map(i => {
        if (i.step !== stepKey) return i
        if (!completed) return { ...i, completed, date: null }
        if (completed && !i.date) {
          const today = new Date().toISOString().slice(0, 10)
          return { ...i, completed, date: today }
        }
        return { ...i, completed }
      })
      this.updateAndEmit(next)
    },
    onDatePicked (stepKey, dateStr) {
      this.$set(this.menus, stepKey, false)
      const next = (this.local || []).map(i => (i.step === stepKey ? { ...i, date: dateStr } : i))
      this.updateAndEmit(next)
    },
    async save () {
      try {
        this.saving = true
        this.$emit('save', this.local) // parent persists to API/store
      } finally {
        this.saving = false
      }
    },
    resetToProp () {
      const next = this.normalize(this.value)
      this.local = next
      this.ensureMenuKeys(next)
      this.$emit('input', next)
    }
  }
}
</script>

<style scoped>
.is-loading {
  opacity: 0.6;
  pointer-events: none; /* blocks interactions while loading */
}
</style>
